import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import { RequireAuth } from "./RequireAuth";
import { RedirectIfAuthed } from "./RedirectIfAuthed";
import { InternetConnectionContextProvider } from "../../context/InternetConnectionContext";
import { ControlledNotificationsProvider } from "../../context/NotificationsContext";
import { PageSpinner } from "../../components/Spinner";
import { localRoutes } from "../../../config";
import { useSendingVisitEventsByLocation } from "../../../analytics/useSendingVisitEventsByLocation";
import { lazyReload } from "../../utils/lazyReload";

const Welcome = lazy(() => lazyReload(() => import("../BetaFlow/Welcome")));
const VideoCallPage = lazy(() => lazyReload(() => import("../VideoCall")));
const NotFoundChannel = lazy(() => lazyReload(() => import("../NotFoundChannel")));
const AccountSetup = lazy(() => lazyReload(() => import("../AccountSetup")));
const InvitePage = lazy(() => lazyReload(() => import("../InvitePage")));
const GetChannel = lazy(() => lazyReload(() => import("../GetChannel")));
const NotAvailableTime = lazy(() => lazyReload(() => import("../NotAvailableTime")));
const FindAvailableChannelPage = lazy(() => lazyReload(() => import("../FindAvailableChannel")));
const HomePage = lazy(() => lazyReload(() => import("../Home")));
const Authentication = lazy(() => lazyReload(() => import("../Authentication")));
const VerificationMessage = lazy(() => lazyReload(() => import("../VerificationMessage")));
const BlockedPermission = lazy(() => lazyReload(() => import("../BlockedPermission")));
const Finish = lazy(() => lazyReload(() => import("../BetaFlow/Finish")));
const BetaFlowSetup = lazy(() => lazyReload(() => import("../BetaFlow/BetaFlowSetup")));
const Success = lazy(() => lazyReload(() => import("../BetaFlow/Success")));
const NewOnboardingWelcome = lazy(() => lazyReload(() => import("../NewOnboarding/Welcome")));
const NewOnboardingPermissions = lazy(() => lazyReload(() => import("../NewOnboarding/Permissions")));

export const AllRoutes = () => {
  useSendingVisitEventsByLocation();

  return (
    <InternetConnectionContextProvider>
      <ControlledNotificationsProvider>
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            <Route
              path={localRoutes.onboarding}
              element={
                <RequireAuth>
                  <NewOnboardingWelcome />
                </RequireAuth>
              }
            />
            <Route
              path={localRoutes.onboardingWelcome}
              element={
                <RequireAuth>
                  <NewOnboardingWelcome />
                </RequireAuth>
              }
            />
            <Route
              path={localRoutes.onboardingPermissions}
              element={
                <RequireAuth>
                  <NewOnboardingPermissions />
                </RequireAuth>
              }
            />

            <Route path={localRoutes.invite} element={<InvitePage />} />

            <Route
              path={localRoutes.accountSetup}
              element={
                <RequireAuth>
                  <AccountSetup />
                </RequireAuth>
              }
            />
            <Route
              path={localRoutes.betaFlow.welcome}
              element={
                <RequireAuth>
                  <Welcome />
                </RequireAuth>
              }
            />
            <Route
              path={localRoutes.betaFlow.steps.specify}
              element={
                <RequireAuth>
                  <BetaFlowSetup />
                </RequireAuth>
              }
            />
            <Route
              path={localRoutes.betaFlow.finish}
              element={
                <RequireAuth>
                  <Finish />
                </RequireAuth>
              }
            />
            <Route
              path={localRoutes.betaFlow.success}
              element={
                <RequireAuth>
                  <Success />
                </RequireAuth>
              }
            />

            <Route
              path={localRoutes.register}
              element={
                <RedirectIfAuthed>
                  <Authentication />
                </RedirectIfAuthed>
              }
            />

            <Route
              path={localRoutes.login}
              element={
                <RedirectIfAuthed>
                  <Authentication />
                </RedirectIfAuthed>
              }
            />

            <Route path={localRoutes.verification} element={<VerificationMessage />} />

            <Route
              path={localRoutes.videoCall}
              element={
                <RequireAuth>
                  <VideoCallPage />
                </RequireAuth>
              }
            />
            <Route
              path={localRoutes.getChannel}
              element={
                <RequireAuth>
                  <GetChannel />
                </RequireAuth>
              }
            />
            <Route
              path={localRoutes.notAvailableTime}
              element={
                <RequireAuth>
                  <NotAvailableTime />
                </RequireAuth>
              }
            />
            <Route
              path={localRoutes.notFoundChannel}
              element={
                <RequireAuth>
                  <NotFoundChannel />
                </RequireAuth>
              }
            />

            <Route
              path={localRoutes.findAvailableChannel}
              element={
                <RequireAuth>
                  <FindAvailableChannelPage />
                </RequireAuth>
              }
            />
            <Route path={localRoutes.blockedPermission} element={<BlockedPermission />} />

            <Route path={localRoutes.home} element={<HomePage />} />
          </Routes>
        </Suspense>
      </ControlledNotificationsProvider>
    </InternetConnectionContextProvider>
  );
};
